import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function newSessionId() {
  return "sess-" + Math.random().toString(36).slice(2) + "-" + Date.now();
}

const STARTER = {
  role: "bot",
  text: "Bonjour ! Je suis l'assistant virtuel de MD Courtage. Comment puis-je vous aider ? (assurance, crédit, placement, RDV…)",
};

const SUGGESTIONS = [
  "Comment fonctionne un courtier ?",
  "J'aimerais un crédit immobilier",
  "Quelle assurance emprunteur choisir ?",
];

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => newSessionId());
  const [messages, setMessages] = useState([STARTER]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setMessages((m) => [...m, { role: "user", text: content }]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: content }),
      });
      const data = await res.json();
      setMessages((m) => [...m, { role: "bot", text: data.reply || "Désolé, je n'ai pas pu répondre. Appelez le 06.88.07.38.33." }]);
    } catch {
      setMessages((m) => [...m, { role: "bot", text: "Petit souci technique. Vous pouvez me joindre directement au 06.88.07.38.33." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="chatbot-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-label="Ouvrir le chat"
        className={`fixed bottom-6 right-6 z-[60] w-16 h-16 rounded-full grid place-items-center text-white shadow-2xl transition-all ${
          open ? "bg-md-primary" : "bg-md-secondary animate-pulse-soft"
        }`}
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      {open && (
        <div data-testid="chatbot-window" className="fixed bottom-24 right-6 z-[60] w-[92vw] max-w-sm bg-white rounded-3xl shadow-2xl border border-md-border overflow-hidden flex flex-col" style={{ height: "560px" }}>
          <div className="bg-md-primary text-white p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-md-secondary grid place-items-center">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="font-serif text-lg">Assistant MD Courtage</div>
              <div className="text-[11px] text-white/70 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
                En ligne — réponse en quelques secondes
              </div>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-md-bg" data-testid="chatbot-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-md-primary text-white rounded-br-md whitespace-pre-wrap"
                      : "bg-white text-md-text border border-md-border rounded-bl-md [&_p+p]:mt-2 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4"
                  }`}
                >
                  {m.role === "user" ? m.text : (
                    <ReactMarkdown components={{ a: (p) => <a {...p} target="_blank" rel="noreferrer" className="underline text-md-primary font-medium" /> }}>
                      {m.text}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-md-border rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                  <Dot /> <Dot delay="150ms" /> <Dot delay="300ms" />
                </div>
              </div>
            )}

            {messages.length === 1 && !loading && (
              <div className="space-y-2 pt-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`chatbot-suggestion-${s.slice(0, 8)}`}
                    onClick={() => send(s)}
                    className="block text-left w-full text-xs px-4 py-2 rounded-full bg-white border border-md-border hover:border-md-secondary hover:text-md-secondary transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="p-4 bg-white border-t border-md-border flex gap-2"
          >
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Écrivez votre question…"
              className="flex-1 bg-md-bg border border-md-border rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-md-secondary"
            />
            <button
              data-testid="chatbot-send"
              type="submit"
              disabled={loading || !input.trim()}
              className="w-11 h-11 rounded-full bg-md-secondary hover:bg-md-secondaryHover disabled:opacity-50 text-white grid place-items-center transition-colors"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function Dot({ delay = "0ms" }) {
  return (
    <span
      className="w-2 h-2 rounded-full bg-md-muted/60 inline-block animate-bounce"
      style={{ animationDelay: delay, animationDuration: "1s" }}
    />
  );
}
